import { normalizeSymbol } from "./instrument";
import { type MarketId, type MarketState, sessionAt } from "./market";
import { formatPrice, type Price, price } from "./money";

/**
 * Market data, canonical form. Every `MarketDataPort` implementation produces
 * exactly these shapes regardless of what its vendor's wire format looks like.
 */

export type Quote = {
  symbol: string;
  /** The venue this price is from. A symbol alone does not identify an instrument. */
  market: MarketId;
  /**
   * Whether that market was open **when the price was taken**, not now.
   *
   * Stamped by `makeQuote` from `asOf`, so no adapter gets its own opinion
   * about it — two providers disagreeing on whether a market was open is
   * precisely the drift the ports exist to prevent. It is a property of the
   * quote rather than a question to ask later, because "was this taken during
   * the session" is unanswerable once the quote has been passed on.
   */
  state: MarketState;
  bid: Price;
  ask: Price;
  /** Last traded price. Between bid and ask under normal conditions. */
  last: Price;
  bidSize: number;
  askSize: number;
  /**
   * Yesterday's closing price, when the venue has one.
   *
   * The reference a ticker measures the day against — `last - previousClose` is
   * what "up 1.4% today" means, and it is the one figure a consumer cannot
   * derive for itself: the first price a browser sees is the first price THAT
   * browser saw, so two people watching the same stock at the same instant
   * would otherwise disagree about which way it had gone.
   *
   * Optional, and honestly so. A provider that does not serve it, a venue with
   * no prior session, and a symbol listed today all have no answer — and a
   * consumer must render "no direction" there rather than a false one. The
   * session's OPEN was considered and rejected: against it everything is flat
   * at the bell and there is no reference at all before it.
   */
  previousClose?: Price;
  asOf: Date;
  /** Which provider produced this, for debugging and staleness attribution. */
  source: string;
};

export type Tick = {
  symbol: string;
  price: Price;
  size: number;
  asOf: Date;
  source: string;
};

export type Bar = {
  symbol: string;
  open: Price;
  high: Price;
  low: Price;
  close: Price;
  volume: number;
  /**
   * The instant this bar became KNOWABLE — its close, not its open.
   *
   * A bar covers `[asOf - TIMEFRAME_MS, asOf)`, and the convention is the whole
   * of what stops a backtest reading a price before it existed. A daily bar
   * stamped at midnight says the day's close was available at midnight, which
   * is a fact about tomorrow; every strategy scored against it looks good, and
   * nothing downstream can tell that from skill.
   *
   * It is documented here rather than left to each adapter because it was left
   * to each adapter and they did not agree: three implementations stamped the
   * open while `quoteFromRecording` — the one function a backtest and a replay
   * share — stamped `bar.t + TIMEFRAME_MS` for exactly the reason above. One
   * fact, one place, the same argument `TIMEFRAME_MS` makes below.
   *
   * A `DateRange` is unaffected and still describes the period COVERED, so a
   * range and a stamp are answering different questions: which bars, and when
   * each one could first have been read.
   */
  asOf: Date;
};

export const TIMEFRAMES = ["1Min", "5Min", "15Min", "1Hour", "1Day"] as const;
export type Timeframe = (typeof TIMEFRAMES)[number];

/**
 * How long one bar of each timeframe covers.
 *
 * Lives here rather than in a provider because two things must agree on it or
 * a replayed day drifts from a backtested one: the provider deciding which bar
 * is current, and the harness deciding when to step. One fact, one place.
 */
export const TIMEFRAME_MS: Record<Timeframe, number> = {
  "1Min": 60_000,
  "5Min": 300_000,
  "15Min": 900_000,
  "1Hour": 3_600_000,
  "1Day": 86_400_000,
};

export type DateRange = { start: Date; end: Date };

export type Subscription = {
  symbols: readonly string[];
  unsubscribe(): Promise<void>;
};

/**
 * The spread must be crossable in the right direction: a buy pays the ask, a
 * sell receives the bid. A provider returning `bid > ask` has a bug, and
 * accepting it silently would let the execution model fill a round trip at a
 * profit with no price movement — the most flattering possible bug in a paper
 * trading system, and therefore the one most likely to go unreported.
 */
export function assertQuoteSane(q: Quote): void {
  if (q.bid > q.ask) {
    throw new Error(`inverted quote from ${q.source} for ${q.symbol}: bid ${q.bid} > ask ${q.ask}`);
  }
  if (q.bid <= 0n || q.ask <= 0n || q.last <= 0n) {
    throw new Error(`non-positive quote from ${q.source} for ${q.symbol}`);
  }
}

/** Midpoint, for display and for marking positions when a side isn't implied. */
export function midpoint(q: Quote): Price {
  return ((q.bid + q.ask) / 2n) as Price;
}

/**
 * How old a print may be and still be the market's current word.
 *
 * `now` is a parameter rather than a clock of this module's own: the domain is
 * pure, and the caller is the one that knows what time it is. `maxAgeMs` comes
 * from the feed's own delay — `maxPrintAgeMs` in `apps/api/src/feed-schedule.ts`
 * — so a deployment that changes its entitlement changes this with it, rather
 * than carrying a second number that happens to agree today.
 */
export type PriceFreshness = {
  now: Date;
  maxAgeMs: number;
};

/**
 * The session a price still speaks for, which is not always the one it was
 * printed in.
 *
 * `state` records that the venue was open when this price printed. That is the
 * right thing to store and the wrong thing to act on by itself, because
 * **nothing about a stored quote stops being true as it ages**. A delayed feed
 * sleeps when the venue shuts, so the last print of a session — always stamped
 * `open`, since the last print of a session is by definition made during it —
 * goes on being served all evening, all weekend, and through a holiday. On
 * 2026-08-19 production served a 19:59:58Z print stamped `open` for thirteen
 * hours while `/api/v1/markets` said `closed`, and `tradableAgainst` would have
 * let an order fill against it.
 *
 * So the stamp means *this print is from a session that is still running*, and
 * age is how that gets decided. Within one quote-delay of the close the answer
 * is still `open` and orders still fill — correct, because those prints were
 * made inside the session and the last of them is the closing price. Past that,
 * the session this price belonged to has ended.
 *
 * **A stale print must not answer `unknown`.** `tradableAgainst` allows
 * `unknown` deliberately — it means "past the calendar horizon", not "shut" —
 * so answering it here would wave every stale price through the guard this
 * exists to close.
 */
export function effectiveState(
  q: Pick<Quote, "state" | "asOf">,
  freshness: PriceFreshness,
): MarketState {
  const ageMs = freshness.now.getTime() - q.asOf.getTime();
  return ageMs > freshness.maxAgeMs ? "closed" : q.state;
}

/** Serialize for JSON responses and Ably messages — decimal strings, never floats. */
export function quoteToWire(q: Quote): Record<string, unknown> {
  return {
    symbol: q.symbol,
    market: q.market,
    market_state: q.state,
    bid: formatPrice(q.bid),
    ask: formatPrice(q.ask),
    last: formatPrice(q.last),
    bid_size: q.bidSize,
    ask_size: q.askSize,
    // Omitted rather than null when the provider has none: a consumer branches
    // on its absence to decide there is no direction to show.
    ...(q.previousClose === undefined ? {} : { previous_close: formatPrice(q.previousClose) }),
    as_of: q.asOf.toISOString(),
    source: q.source,
  };
}

/**
 * The same quote, told to somebody outside.
 *
 * **`source` is the provider's id, and `kind` is what sort of prices those
 * are.** Both travel, since `M4/E30`. This used to overwrite `source` with the
 * label so no vendor's name left the building — right while the platform chose
 * the feed and nobody else could, and wrong the moment a trader could paste
 * their own key: somebody priced from their own Alpaca entitlement is entitled
 * to see that Alpaca priced it. `kind` is what survives of the old rule,
 * because whether these are real prices is still worth one word.
 *
 * The swap is *deleted* rather than moved. `quoteToWire` is also the payload
 * `MarketFeedDO` caches, and the cache reads `source` back to discard a quote
 * left by a provider this deployment no longer uses — so the id was already on
 * the stored wire all along, and the public shape now simply stops hiding it.
 *
 * `kind` is a parameter rather than a lookup because this package must not know
 * one vendor's name from another's: see `ProviderCapabilities.label`.
 *
 * **`market_state` is still re-derived here**, and that is the one thing the
 * stored wire and the public one must go on disagreeing about: the cache keeps
 * the print's own stamp so it can reason about the quote it holds, while a
 * caller has to be told what that print means *now*. A cached `market_state`
 * and a caller reading it hours later can only agree by accident.
 */
export function quoteToPublicWire(
  q: Quote,
  kind: string,
  freshness: PriceFreshness,
): Record<string, unknown> {
  return { ...quoteToWire(q), market_state: effectiveState(q, freshness), kind };
}

/**
 * Rebuild a quote from the shape `quoteToWire` produced.
 *
 * Exists because a cached quote has to cross a Durable Object boundary, and the
 * scaled-integer `Price` and the `Date` are not what you want to hand a storage
 * layer. The round trip goes through `makeQuote`, so the rebuilt quote is
 * re-checked by `assertQuoteSane` and its `state` is re-derived from the
 * calendar rather than restored from the payload — a stored `market_state` and
 * a stored `as_of` can only disagree once, and the calendar is the one that is
 * right.
 */
export function quoteFromWire(wire: Record<string, unknown>): Quote {
  return makeQuote({
    symbol: wire.symbol as string,
    market: wire.market as MarketId,
    bid: wire.bid as string,
    ask: wire.ask as string,
    last: wire.last as string,
    bidSize: wire.bid_size as number,
    askSize: wire.ask_size as number,
    previousClose: wire.previous_close as string | undefined,
    asOf: new Date(wire.as_of as string),
    source: wire.source as string,
  });
}

export function makeQuote(input: {
  symbol: string;
  /** Which venue. Required, because a defaulted market is a guess nobody sees. */
  market: MarketId;
  bid: string;
  ask: string;
  last: string;
  bidSize?: number;
  askSize?: number;
  previousClose?: string | undefined;
  asOf: Date;
  source: string;
}): Quote {
  const q: Quote = {
    symbol: normalizeSymbol(input.symbol),
    market: input.market,
    // Derived here and only here. An adapter that computed its own would agree
    // today and drift the first time a calendar changed.
    state: sessionAt(input.market, input.asOf).state,
    bid: price(input.bid),
    ask: price(input.ask),
    last: price(input.last),
    bidSize: input.bidSize ?? 0,
    askSize: input.askSize ?? 0,
    // Spread rather than defaulted: under exactOptionalPropertyTypes an
    // explicit `undefined` is not the same as absent, and "this provider does
    // not serve a previous close" is exactly the thing that must stay absent.
    ...(input.previousClose === undefined ? {} : { previousClose: price(input.previousClose) }),
    asOf: input.asOf,
    source: input.source,
  };
  assertQuoteSane(q);
  return q;
}
